import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../../../../../../api';

const EquipmentChecklistHistoryListModal = ({ equipmentId, onViewDetails, refreshTrigger }) => {
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const authToken = sessionStorage.getItem('auth_token');

  const fetchChecklistHistory = useCallback(async () => {
    if (!equipmentId || !authToken) {
      setError('Missing equipment ID or authentication.');
      setHistory([]);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const response = await axiosInstance.get(
        `ems/get-equipment-checklist-history/${equipmentId}`,
        {
          headers: {
            Accept: 'application/vnd.api+json',
            'Content-Type': 'application/vnd.api+json',
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      // Extract data from JSON:API format: { status: "success", data: [...] }
      const items = response.data?.data || [];

      if (!Array.isArray(items) || items.length === 0) {
        setHistory([]);
        return;
      }

      // Group by submission_batch_id
      const grouped = items.reduce((acc, item) => {
        const batchId = item.submission_batch_id;
        const submittedAt = item.submitted_at || item.created_at;

        if (!acc[batchId]) {
          acc[batchId] = {
            submission_id: batchId,
            checked_date: submittedAt,
            checked_by: item.user
              ? `${item.user.first_name} ${item.user.last_name}`.trim() || 'Unknown User'
              : 'Unknown User',
          };
        }
        return acc;
      }, {});

      // Convert to array and sort by date (newest first)
      const formattedData = Object.values(grouped).sort((a, b) =>
        new Date(b.checked_date) - new Date(a.checked_date)
      );

      setHistory(formattedData);
    } catch (err) {
      console.error('Error fetching checklist history:', err);
      setError('Failed to load history. Please try again.');
      setHistory([]);
    } finally {
      setIsLoading(false);
    }
  }, [equipmentId, authToken]);

  // Fetch on mount, equipmentId change, or refreshTrigger
  useEffect(() => {
    fetchChecklistHistory();
  }, [fetchChecklistHistory, refreshTrigger]);

  // Refresh when modal is opened
  useEffect(() => {
    const modalEl = document.getElementById('bs-equipment-checklist-history-list');
    if (!modalEl) return;

    const handleShow = () => fetchChecklistHistory();

    modalEl.addEventListener('show.bs.modal', handleShow);
    return () => modalEl.removeEventListener('show.bs.modal', handleShow);
  }, [fetchChecklistHistory]);

  return (
    <div
      className="modal fade"
      id="bs-equipment-checklist-history-list"
      tabIndex="-1"
      aria-labelledby="equipmentChecklistHistoryLabel"
      aria-hidden="true"
    >
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          {/* Header */}
          <div className="modal-header">
            <h5 className="modal-title" id="equipmentChecklistHistoryLabel">
              Equipment Checklist History
            </h5>
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>

          {/* Body */}
          <div className="modal-body">
            {isLoading ? (
              <div className="text-center py-4">Loading Checklist History...</div>
            ) : error ? (
              <div className="text-center py-4 text-danger">{error}</div>
            ) : history.length === 0 ? (
              <div className="text-center py-4">No Checklist History Found</div>
            ) : (
              <div className="table-responsive">
                <table className="table text-nowrap customize-table mb-0 align-middle">
                  <thead className="text-dark fs-4">
                    <tr>
                      <th><h6 className="fw-semibold mb-0">Checked Date</h6></th>
                      <th><h6 className="fw-semibold mb-0">Checked By</h6></th>
                      <th><h6 className="fw-semibold mb-0">Action</h6></th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((item) => (
                      <tr key={item.submission_id}>
                        <td>
                          {item.checked_date
                            ? new Date(item.checked_date).toLocaleString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : 'N/A'}
                        </td>
                        <td>{item.checked_by}</td>
                        <td>
                          <button
                            onClick={() => onViewDetails(item.submission_id)}
                            className="h4 border-0 bg-transparent"
                            title="View Details"
                          >
                            <i className="ti ti-eye text-primary"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              data-bs-dismiss="modal"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EquipmentChecklistHistoryListModal;